import { axiosRubyClient } from '@/util/axios/axiosClient';
import {
  Post,
  PostFormData,
  PostsIndexResponse,
  PostResponse,
  PostsFilters,
  Pagination,
} from '@/types/posts';

export interface UploadUrlResponse {
  key: string;
  url: string;
  public_url: string;
  bucket: string;
  content_type: string;
}

export class PostsAPI {
  /**
   * Get presigned URL for uploading image to S3
   */
  static async getUploadUrl(
    filename: string,
    contentType?: string,
  ): Promise<UploadUrlResponse> {
    const params = new URLSearchParams();
    params.append('filename', filename);
    if (contentType) params.append('content_type', contentType);

    const response = await axiosRubyClient.get(
      `/api/admin/posts/upload_url?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get all posts with filters and pagination
   */
  static async getPosts(
    filters?: PostsFilters,
    pagination?: Pagination,
  ): Promise<PostsIndexResponse> {
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);

    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.per_page)
      params.append('per_page', pagination.per_page.toString());

    const response = await axiosRubyClient.get(
      `/api/admin/posts?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get a single post by ID
   */
  static async getPost(id: number): Promise<PostResponse> {
    const response = await axiosRubyClient.get(`/api/admin/posts/${id}`);
    return response.data;
  }

  /**
   * Create a new post
   */
  static async createPost(data: PostFormData): Promise<PostResponse> {
    const response = await axiosRubyClient.post('/api/admin/posts', {
      post: data,
    });
    return response.data;
  }

  /**
   * Update an existing post
   */
  static async updatePost(
    id: number,
    data: Partial<PostFormData>,
  ): Promise<PostResponse> {
    const response = await axiosRubyClient.patch(`/api/admin/posts/${id}`, {
      post: data,
    });
    return response.data;
  }

  /**
   * Delete a post
   */
  static async deletePost(id: number): Promise<{ success: boolean }> {
    const response = await axiosRubyClient.delete(`/api/admin/posts/${id}`);
    return response.data;
  }

  /**
   * Publish a post
   */
  static async publishPost(id: number): Promise<PostResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/posts/${id}/publish`,
    );
    return response.data;
  }

  /**
   * Unpublish a post (set to draft)
   */
  static async unpublishPost(id: number): Promise<PostResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/posts/${id}/unpublish`,
    );
    return response.data;
  }
}
